/**
 * Chart Theme Utility
 * Provides theme-aware colors for all Insights dashboard chart components.
 * Uses Ant Design tokens so colors adapt to light/dark mode automatically.
 */

import { useMemo } from 'react';
import { theme } from 'antd';

function isColorDark(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  // Relative luminance
  return (0.299 * r + 0.587 * g + 0.114 * b) < 128;
}

export const PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

export interface ChartTheme {
  isDark: boolean;
  // Chart palette (constant across themes)
  PALETTE: string[];
  // Axis & grid
  axisText: string;
  gridLine: string;
  xAxisLine: string;
  // Tooltip
  tooltipBg: string;
  tooltipBorder: string;
  tooltipShadow: string;
  // Legend
  legendHidden: string;
  // Text
  mutedText: string;
  secondaryText: string;
  // Backgrounds
  subtleBg: string;
  cardBg: string;
  borderColor: string;
  // Table
  tableHeaderBg: string;
  tableHeaderText: string;
  tableStripeBg: string;
  // Badges
  positiveBg: string;
  positiveText: string;
  negativeBg: string;
  negativeText: string;
  rankBadgeBg: string;
  rankBadgeText: string;
  // Heatmap
  heatmapEmpty: string;
  heatmapLow: string;
  heatmapMed: string;
  heatmapHigh: string;
  heatmapOutside: string;
  heatmapCellBorder: string;
  // Bar chart backgrounds
  barTrackBg: string;
}

export function useChartTheme(): ChartTheme {
  const { token } = theme.useToken();

  return useMemo(() => {
    const isDark = isColorDark(token.colorBgBase);

    return {
      isDark,
      PALETTE,
      // Axis & grid
      axisText: token.colorTextTertiary,
      gridLine: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
      xAxisLine: token.colorBorderSecondary,
      // Tooltip
      tooltipBg: isDark ? token.colorBgElevated : '#fff',
      tooltipBorder: token.colorBorderSecondary,
      tooltipShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
      // Legend
      legendHidden: token.colorTextQuaternary,
      // Text
      mutedText: token.colorTextTertiary,
      secondaryText: token.colorTextSecondary,
      // Backgrounds
      subtleBg: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
      cardBg: token.colorBgContainer,
      borderColor: token.colorBorderSecondary,
      // Table
      tableHeaderBg: isDark ? 'rgba(255,255,255,0.04)' : '#f8fafc',
      tableHeaderText: token.colorTextTertiary,
      tableStripeBg: token.colorBgLayout,
      // Badges
      positiveBg: isDark ? 'rgba(22,163,106,0.15)' : '#dcfce7',
      positiveText: isDark ? '#4ade80' : '#16a34a',
      negativeBg: isDark ? 'rgba(220,38,38,0.15)' : '#fee2e2',
      negativeText: isDark ? '#f87171' : '#dc2626',
      rankBadgeBg: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
      rankBadgeText: token.colorTextSecondary,
      // Heatmap
      heatmapEmpty: isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0',
      heatmapLow: isDark ? '#1e3a5f' : '#93c5fd',
      heatmapMed: '#3b82f6',
      heatmapHigh: '#1d4ed8',
      heatmapOutside: isDark ? 'rgba(255,255,255,0.02)' : '#f8fafc',
      heatmapCellBorder: isDark ? 'rgba(255,255,255,0.08)' : 'transparent',
      // Bar chart
      barTrackBg: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
    };
  }, [token]);
}

/** Recharts tooltip content style from chart theme */
export function tooltipStyle(ct: ChartTheme) {
  return {
    background: ct.tooltipBg,
    border: `1px solid ${ct.tooltipBorder}`,
    borderRadius: 8,
    fontSize: 12,
    boxShadow: ct.tooltipShadow,
  };
}

/** Recharts axis tick style from chart theme */
export function axisTickStyle(ct: ChartTheme) {
  return { fontSize: 11, fill: ct.axisText };
}

/** Recharts CartesianGrid props from chart theme */
export function gridProps(ct: ChartTheme) {
  return { strokeDasharray: '3 3', stroke: ct.gridLine };
}
